'use client';

import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { useConsent } from '../../contexts/ConsentContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { usePlayer } from '../../contexts/PlayerContext';
import { useTheme } from '../../contexts/ThemeContext';
import releaseData from '../../data/releaseData';
import type { Release as ReleaseType } from '../../types';
import { getBasePath } from '../../utils/basePath';
import JoevLogo from '../JoevLogo';
import { unlockAudioContext } from '../SoundCloudPlayer/SoundCloudPlayer';

const NavHeader = styled.header<{ $visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 64px;
  z-index: 9980;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  background: var(--nav-bg, rgba(8, 8, 16, 0.9));
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-bottom: 1px solid var(--nav-border, rgba(255, 0, 255, 0.25));
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  transform: ${({ $visible }) => ($visible ? 'translateY(0)' : 'translateY(-100%)')};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? 'all' : 'none')};
  transition:
    transform 0.35s cubic-bezier(0.16, 1, 0.3, 1),
    opacity 0.3s ease,
    background-color 0.3s ease,
    border-color 0.3s ease;

  @media (max-width: 768px) {
    height: 56px;
    padding: 0 16px;
  }
`;

const LogoLink = styled.a`
  display: flex;
  align-items: center;
  text-decoration: none;
  cursor: pointer;
  height: 32px;
  transition: transform 0.2s ease, opacity 0.2s ease;

  svg {
    height: 26px;
    width: auto;
    display: block;
  }

  &:hover {
    transform: scale(1.05);
    opacity: 0.9;
  }

  @media (max-width: 768px) {
    height: 28px;
    svg {
      height: 22px;
    }
  }
`;

const NavLinksList = styled.nav`
  display: flex;
  align-items: center;
  gap: 6px;

  @media (max-width: 900px) {
    display: none;
  }
`;

const NavItem = styled.a<{ $active: boolean }>`
  padding: 6px 14px;
  border-radius: 16px;
  font-size: 0.85rem;
  font-variant-caps: all-petite-caps;
  letter-spacing: 1px;
  text-decoration: none;
  font-weight: ${({ $active }) => ($active ? '700' : '500')};
  color: ${({ $active }) => ($active ? 'var(--accent-green, #00ff00)' : 'var(--text-secondary, rgba(255, 255, 255, 0.75))')};
  background: ${({ $active }) => ($active ? 'rgba(0, 255, 0, 0.12)' : 'transparent')};
  border: 1px solid ${({ $active }) => ($active ? 'rgba(0, 255, 0, 0.4)' : 'transparent')};
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    color: #ff2d95;
    background: rgba(255, 45, 149, 0.1);
    border-color: rgba(255, 45, 149, 0.3);
  }
`;

const ReleasesNavWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const ReleasesNavItem = styled.div<{ $active: boolean; $open: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 16px;
  background: ${({ $active, $open }) =>
    $open ? 'rgba(255, 45, 149, 0.15)' : $active ? 'rgba(0, 255, 0, 0.12)' : 'transparent'};
  border: 1px solid
    ${({ $active, $open }) =>
      $open ? 'rgba(255, 45, 149, 0.4)' : $active ? 'rgba(0, 255, 0, 0.4)' : 'transparent'};
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;

  &:hover {
    color: #ff2d95;
    background: rgba(255, 45, 149, 0.1);
    border-color: rgba(255, 45, 149, 0.3);
  }
`;

const ReleaseTextGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.1;
`;

const ReleaseMainLabel = styled.span<{ $active: boolean }>`
  font-size: 0.85rem;
  font-variant-caps: all-petite-caps;
  letter-spacing: 1px;
  font-weight: ${({ $active }) => ($active ? '700' : '500')};
  color: ${({ $active }) =>
    $active ? 'var(--accent-green, #00ff00)' : 'var(--text-secondary, rgba(255, 255, 255, 0.75))'};
  transition: color 0.2s;
`;

const ReleaseSubline = styled.span`
  font-size: 0.62rem;
  font-variant-caps: all-petite-caps;
  letter-spacing: 0.5px;
  color: #ff2d95;
  font-weight: 700;
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: -1px;
`;

const ChevronIcon = styled.span<{ $open: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: inherit;
  transition: transform 0.25s ease;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0deg)')};

  svg {
    width: 11px;
    height: 11px;
  }
`;

const ReleasesDropdown = styled.div<{ $visible: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  min-width: 300px;
  max-height: 380px;
  overflow-y: auto;
  background: var(--nav-bg, rgba(10, 10, 20, 0.98));
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid var(--nav-border, rgba(255, 0, 255, 0.3));
  border-radius: 16px;
  padding: 8px;
  box-shadow:
    0 12px 40px var(--card-shadow, rgba(0, 0, 0, 0.5)),
    0 0 20px rgba(0, 255, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: 4px;
  z-index: 9999;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: ${({ $visible }) => ($visible ? 'translateY(0) scale(1)' : 'translateY(-8px) scale(0.96)')};
  pointer-events: ${({ $visible }) => ($visible ? 'all' : 'none')};
  transition:
    opacity 0.2s ease,
    transform 0.2s ease,
    background-color 0.3s ease,
    border-color 0.3s ease;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 255, 0, 0.4);
    border-radius: 2px;
  }
`;

const DropdownItem = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 8px;
  border-radius: 10px;
  background: ${({ $active }) => ($active ? 'rgba(0, 255, 0, 0.12)' : 'transparent')};
  border: 1px solid ${({ $active }) => ($active ? 'rgba(0, 255, 0, 0.4)' : 'transparent')};
  cursor: pointer;
  transition: all 0.15s ease;
  gap: 8px;

  &:hover {
    background: rgba(255, 45, 149, 0.12);
    border-color: rgba(255, 45, 149, 0.35);
  }
`;

const DropdownItemLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
`;

const ReleaseThumb = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  object-fit: cover;
  border: 1px solid rgba(255, 255, 255, 0.15);
  flex-shrink: 0;
`;

const ReleaseItemInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.2;
`;

const ReleaseItemTitle = styled.span<{ $active: boolean }>`
  font-size: 0.82rem;
  font-weight: 600;
  font-variant-caps: all-petite-caps;
  color: ${({ $active }) => ($active ? '#00ff00' : 'var(--text-primary, #fff)')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ReleaseItemDate = styled.span`
  font-size: 0.65rem;
  color: var(--text-muted, rgba(255, 255, 255, 0.5));
  font-variant-caps: all-petite-caps;
`;

const ReleasePlayBtn = styled.button<{ $playing: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid ${({ $playing }) => ($playing ? '#00ff00' : '#ff2d95')};
  background: ${({ $playing }) => ($playing ? '#00ff00' : 'rgba(255, 45, 149, 0.15)')};
  color: ${({ $playing }) => ($playing ? '#000000' : '#ff2d95')};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  outline: none;
  flex-shrink: 0;
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.15);
    background: ${({ $playing }) => ($playing ? '#00ff00' : '#ff2d95')};
    color: ${({ $playing }) => ($playing ? '#000000' : '#ffffff')};
    box-shadow: 0 0 10px ${({ $playing }) => ($playing ? 'rgba(0, 255, 0, 0.5)' : 'rgba(255, 45, 149, 0.5)')};
  }

  svg {
    width: 12px;
    height: 12px;
  }
`;

const ControlsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  @media (max-width: 768px) {
    gap: 8px;
  }
`;

const LangButton = styled.button<{ $active: boolean }>`
  background: ${({ $active }) => ($active ? '#00ff00' : 'transparent')};
  color: ${({ $active }) => ($active ? '#000' : 'var(--text-secondary, rgba(255, 255, 255, 0.75))')};
  border: none;
  border-radius: 12px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  font-variant-caps: all-petite-caps;
  padding: 3px 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: ${({ $active }) => ($active ? '#000' : '#ff2d95')};
  }
`;

const LangPill = styled.div`
  display: inline-flex;
  align-items: center;
  background: var(--card-bg, rgba(14, 14, 24, 0.85));
  border: 1px solid var(--nav-border, rgba(255, 0, 255, 0.25));
  border-radius: 16px;
  padding: 2px 4px;
`;

const ThemeNavBtn = styled.button`
  background: var(--card-bg, rgba(14, 14, 24, 0.85));
  border: 1px solid var(--nav-border, rgba(255, 0, 255, 0.25));
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ff2d95;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #00ff00;
    border-color: rgba(0, 255, 0, 0.5);
    transform: scale(1.08);
  }

  svg {
    width: 15px;
    height: 15px;
  }
`;

const HamburgerBtn = styled.button<{ $isOpen: boolean }>`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  color: var(--text-primary, #fff);
  outline: none;
  z-index: 9995;

  @media (max-width: 900px) {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 4px;
  }

  span {
    display: block;
    width: 22px;
    height: 2px;
    background: currentColor;
    border-radius: 1px;
    transition: all 0.3s ease;
  }

  ${({ $isOpen }) =>
    $isOpen &&
    css`
      span:nth-child(1) {
        transform: rotate(45deg) translate(4px, 4.5px);
        background: #00ff00;
      }
      span:nth-child(2) {
        opacity: 0;
        transform: translateX(6px);
      }
      span:nth-child(3) {
        transform: rotate(-45deg) translate(4px, -4.5px);
        background: #00ff00;
      }
    `}
`;

const MobileDrawer = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 9985;
  background: var(--nav-bg, rgba(8, 8, 16, 0.98));
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  display: flex;
  flex-direction: column;
  padding: 70px 24px 30px;
  transform: ${({ $isOpen }) => ($isOpen ? 'translateY(0)' : 'translateY(-100%)')};
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  pointer-events: ${({ $isOpen }) => ($isOpen ? 'all' : 'none')};
  transition:
    transform 0.4s cubic-bezier(0.16, 1, 0.3, 1),
    opacity 0.3s ease;

  @media (min-width: 901px) {
    display: none;
  }
`;

const MobileNavList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 16px;
  flex: 1;
  overflow-y: auto;
`;

const MobileNavItem = styled.a<{ $active: boolean }>`
  padding: 14px 18px;
  border-radius: 12px;
  font-size: 1.15rem;
  font-variant-caps: all-petite-caps;
  letter-spacing: 1.5px;
  text-decoration: none;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${({ $active }) => ($active ? 'var(--accent-green, #00ff00)' : 'var(--text-primary, #fff)')};
  background: ${({ $active }) => ($active ? 'rgba(0, 255, 0, 0.12)' : 'var(--card-bg, rgba(255, 255, 255, 0.03))')};
  border: 1px solid
    ${({ $active }) => ($active ? 'rgba(0, 255, 0, 0.4)' : 'var(--card-border, rgba(255, 255, 255, 0.08))')};
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 45, 149, 0.12);
    border-color: rgba(255, 45, 149, 0.4);
    color: #ff2d95;
  }
`;

const MobileReleaseAccordion = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-left: 12px;
  margin-top: -4px;
  margin-bottom: 6px;
`;

const MobileReleaseRow = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-radius: 10px;
  background: ${({ $active }) => ($active ? 'rgba(0, 255, 0, 0.1)' : 'rgba(255, 255, 255, 0.03)')};
  border: 1px solid ${({ $active }) => ($active ? 'rgba(0, 255, 0, 0.35)' : 'rgba(255, 255, 255, 0.06)')};
  cursor: pointer;
`;

const MobileControlsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 16px;
  border-top: 1px solid var(--card-border, rgba(255, 255, 255, 0.1));
`;

const NAV_SECTIONS = [
  { id: 'releases', key: 'navReleases' as const },
  { id: 'soundcloud', key: 'navSoundCloud' as const },
  { id: 'bandcamp-releases', key: 'navBandcamp' as const },
  { id: 'youtube-videos', key: 'navYouTube' as const },
  { id: 'telegram-writing', key: 'navTelegram' as const },
];

export function getReleaseSoundCloudUrl(
  release: ReleaseType
): { url: string; title: string } | null {
  const firstTrack = release.tracklist?.[0];
  if (
    firstTrack?.directLink &&
    typeof firstTrack.directLink === 'object' &&
    'soundcloud' in firstTrack.directLink &&
    firstTrack.directLink.soundcloud
  ) {
    return {
      url: firstTrack.directLink.soundcloud,
      title: `${release.name} - ${firstTrack.trackname}`,
    };
  }
  const scLink = release.links?.find((l) => 'soundcloud' in l);
  if (scLink && 'soundcloud' in scLink && scLink.soundcloud) {
    return {
      url: scLink.soundcloud,
      title: release.name,
    };
  }
  return null;
}

/**
 * Scroll-triggered navigation bar with active release subline, direct release jump dropdown, and instant play buttons.
 */
export const Navigation: React.FC = () => {
  const { t, language, setLanguage } = useLanguage();
  const { isDark, toggleTheme } = useTheme();
  const { playTrack, currentTrackUrl, isPlaying, togglePlay } = usePlayer();
  const { preferences, requestSoundCloudPermission } = useConsent();

  const [isVisible, setIsVisible] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileReleasesOpen, setIsMobileReleasesOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string>('');
  const [activeRelease, setActiveRelease] = useState<ReleaseType | null>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const basePath = getBasePath();

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Scroll detection, active section tracking, and active release viewport detection
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY || window.pageYOffset;
      const threshold = window.innerHeight * 0.45;
      setIsVisible(scrollY > threshold);

      const headerOffset = 120;
      const scrollPosition = scrollY + headerOffset;

      const sections = NAV_SECTIONS.map(({ id }) => {
        const el = document.getElementById(id);
        if (!el) return null;
        const top = el.offsetTop;
        const height = el.offsetHeight;
        return { id, top, bottom: top + height };
      }).filter((s): s is { id: string; top: number; bottom: number } => Boolean(s));

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (scrollPosition >= section.top) {
          setActiveSection(section.id);
          break;
        }
      }

      if (sections.length > 0 && scrollPosition < sections[0].top) {
        setActiveSection('');
      }

      // Detect which specific release is currently in viewport
      const releaseElements = releaseData.map((r) => ({
        release: r,
        el: document.getElementById(`release-${r.detail_link}`),
      }));

      let currentReleaseInView: ReleaseType | null = null;
      const viewportCenter = scrollY + window.innerHeight * 0.45;

      for (let i = 0; i < releaseElements.length; i++) {
        const { release, el } = releaseElements[i];
        if (!el) continue;
        const top = el.offsetTop;
        const bottom = top + el.offsetHeight;
        if (viewportCenter >= top && viewportCenter < bottom) {
          currentReleaseInView = release;
          break;
        }
      }

      if (currentReleaseInView) {
        setActiveRelease(currentReleaseInView);
      } else if (activeSection === 'releases') {
        setActiveRelease(releaseData[0] ?? null);
      } else {
        setActiveRelease(null);
      }
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [activeSection]);

  const handleNavClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    setIsDropdownOpen(false);

    if (targetId === 'top') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const el = document.getElementById(targetId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const handleReleaseJump = (release: ReleaseType) => {
    setIsDropdownOpen(false);
    setIsMobileMenuOpen(false);
    const el = document.getElementById(`release-${release.detail_link}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handlePlayRelease = (release: ReleaseType, e: React.MouseEvent) => {
    e.stopPropagation();
    unlockAudioContext();
    const scInfo = getReleaseSoundCloudUrl(release);
    if (!scInfo) return;

    if (currentTrackUrl === scInfo.url) {
      togglePlay();
      return;
    }

    if (!preferences.soundCloud) {
      const allowed = requestSoundCloudPermission(scInfo.url, scInfo.title, () => {
        unlockAudioContext();
        playTrack(scInfo.url, scInfo.title);
      });
      if (!allowed) return;
    }

    playTrack(scInfo.url, scInfo.title);
  };

  return (
    <>
      <NavHeader $visible={isVisible} role="banner">
        <LogoLink
          href="#top"
          onClick={(e) => handleNavClick(e, 'top')}
          aria-label="joev noise - back to top"
        >
          <JoevLogo />
        </LogoLink>

        <NavLinksList role="navigation" aria-label="Main menu">
          {NAV_SECTIONS.map(({ id, key }) => {
            if (id === 'releases') {
              const isReleasesActive = activeSection === 'releases';
              return (
                <ReleasesNavWrapper key={id} ref={dropdownRef}>
                  <ReleasesNavItem
                    $active={isReleasesActive}
                    $open={isDropdownOpen}
                    onClick={() => setIsDropdownOpen((prev) => !prev)}
                    role="button"
                    tabIndex={0}
                    aria-expanded={isDropdownOpen}
                    aria-haspopup="true"
                    aria-label={`Releases menu${activeRelease ? `: currently viewing ${activeRelease.name}` : ''}`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setIsDropdownOpen((prev) => !prev);
                      }
                    }}
                  >
                    <ReleaseTextGroup>
                      <ReleaseMainLabel $active={isReleasesActive}>
                        {t('navReleases')}
                      </ReleaseMainLabel>
                      {activeRelease && <ReleaseSubline>{activeRelease.name}</ReleaseSubline>}
                    </ReleaseTextGroup>
                    <ChevronIcon $open={isDropdownOpen}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </ChevronIcon>
                  </ReleasesNavItem>

                  <ReleasesDropdown $visible={isDropdownOpen} role="menu" aria-label="All Releases">
                    {releaseData.map((release) => {
                      const isActive = activeRelease?.detail_link === release.detail_link;
                      const scInfo = getReleaseSoundCloudUrl(release);
                      const isTrackPlaying = isPlaying && scInfo?.url === currentTrackUrl;

                      return (
                        <DropdownItem
                          key={release.detail_link}
                          $active={isActive}
                          onClick={() => handleReleaseJump(release)}
                          role="menuitem"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleReleaseJump(release);
                            }
                          }}
                        >
                          <DropdownItemLeft>
                            <ReleaseThumb
                              src={`${basePath}/assets/${release.cover}`}
                              alt={release.name}
                            />
                            <ReleaseItemInfo>
                              <ReleaseItemTitle $active={isActive}>{release.name}</ReleaseItemTitle>
                              <ReleaseItemDate>
                                {release.date}{' '}
                                {release.tracklist.length > 1
                                  ? `· ${release.tracklist.length} tracks`
                                  : ''}
                              </ReleaseItemDate>
                            </ReleaseItemInfo>
                          </DropdownItemLeft>

                          {scInfo && (
                            <ReleasePlayBtn
                              type="button"
                              $playing={isTrackPlaying}
                              onClick={(e) => handlePlayRelease(release, e)}
                              aria-label={`${isTrackPlaying ? 'Pause' : 'Play'} ${release.name}`}
                              title={`${isTrackPlaying ? 'Pause' : 'Play'} ${release.name}`}
                            >
                              {isTrackPlaying ? (
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                  <rect x="6" y="4" width="4" height="16" />
                                  <rect x="14" y="4" width="4" height="16" />
                                </svg>
                              ) : (
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                  <polygon points="6,4 18,12 6,20" />
                                </svg>
                              )}
                            </ReleasePlayBtn>
                          )}
                        </DropdownItem>
                      );
                    })}
                  </ReleasesDropdown>
                </ReleasesNavWrapper>
              );
            }

            return (
              <NavItem
                key={id}
                href={`#${id}`}
                $active={activeSection === id}
                onClick={(e) => handleNavClick(e, id)}
              >
                {t(key)}
              </NavItem>
            );
          })}
        </NavLinksList>

        <ControlsGroup>
          <LangPill role="group" aria-label={t('selectLanguage')}>
            <LangButton
              type="button"
              $active={language === 'en'}
              onClick={() => setLanguage('en')}
              aria-label="English"
            >
              EN
            </LangButton>
            <LangButton
              type="button"
              $active={language === 'de'}
              onClick={() => setLanguage('de')}
              aria-label="Deutsch"
            >
              DE
            </LangButton>
          </LangPill>

          <ThemeNavBtn
            type="button"
            onClick={toggleTheme}
            aria-label={isDark ? t('toggleThemeLight') : t('toggleThemeDark')}
            title={isDark ? t('toggleThemeLight') : t('toggleThemeDark')}
          >
            {isDark ? (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="5" />
                <line x1="12" y1="1" x2="12" y2="3" />
                <line x1="12" y1="21" x2="12" y2="23" />
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                <line x1="1" y1="12" x2="3" y2="12" />
                <line x1="21" y1="12" x2="23" y2="12" />
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
              </svg>
            ) : (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
              </svg>
            )}
          </ThemeNavBtn>

          <HamburgerBtn
            type="button"
            $isOpen={isMobileMenuOpen}
            onClick={() => setIsMobileMenuOpen((prev) => !prev)}
            aria-label={isMobileMenuOpen ? t('menuClose') : t('menuOpen')}
            aria-expanded={isMobileMenuOpen}
          >
            <span />
            <span />
            <span />
          </HamburgerBtn>
        </ControlsGroup>
      </NavHeader>

      {/* Mobile drawer overlay */}
      <MobileDrawer $isOpen={isMobileMenuOpen} role="dialog" aria-modal="true">
        <MobileNavList>
          {NAV_SECTIONS.map(({ id, key }) => {
            if (id === 'releases') {
              const isReleasesActive = activeSection === 'releases';
              return (
                <div key={`m-${id}`}>
                  <MobileNavItem
                    href={`#${id}`}
                    $active={isReleasesActive}
                    onClick={(e) => {
                      e.preventDefault();
                      setIsMobileReleasesOpen((prev) => !prev);
                    }}
                  >
                    <div>
                      <span>{t(key)}</span>
                      {activeRelease && (
                        <div style={{ fontSize: '0.72rem', color: '#ff2d95', fontWeight: 600 }}>
                          {activeRelease.name}
                        </div>
                      )}
                    </div>
                    <ChevronIcon $open={isMobileReleasesOpen}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </ChevronIcon>
                  </MobileNavItem>

                  {isMobileReleasesOpen && (
                    <MobileReleaseAccordion>
                      {releaseData.map((release) => {
                        const isActive = activeRelease?.detail_link === release.detail_link;
                        const scInfo = getReleaseSoundCloudUrl(release);
                        const isTrackPlaying = isPlaying && scInfo?.url === currentTrackUrl;

                        return (
                          <MobileReleaseRow
                            key={`m-rel-${release.detail_link}`}
                            $active={isActive}
                            onClick={() => handleReleaseJump(release)}
                          >
                            <DropdownItemLeft>
                              <ReleaseThumb
                                src={`${basePath}/assets/${release.cover}`}
                                alt={release.name}
                              />
                              <ReleaseItemInfo>
                                <ReleaseItemTitle $active={isActive}>
                                  {release.name}
                                </ReleaseItemTitle>
                                <ReleaseItemDate>{release.date}</ReleaseItemDate>
                              </ReleaseItemInfo>
                            </DropdownItemLeft>

                            {scInfo && (
                              <ReleasePlayBtn
                                type="button"
                                $playing={isTrackPlaying}
                                onClick={(e) => handlePlayRelease(release, e)}
                                aria-label={`${isTrackPlaying ? 'Pause' : 'Play'} ${release.name}`}
                              >
                                {isTrackPlaying ? (
                                  <svg viewBox="0 0 24 24" fill="currentColor">
                                    <rect x="6" y="4" width="4" height="16" />
                                    <rect x="14" y="4" width="4" height="16" />
                                  </svg>
                                ) : (
                                  <svg viewBox="0 0 24 24" fill="currentColor">
                                    <polygon points="6,4 18,12 6,20" />
                                  </svg>
                                )}
                              </ReleasePlayBtn>
                            )}
                          </MobileReleaseRow>
                        );
                      })}
                    </MobileReleaseAccordion>
                  )}
                </div>
              );
            }

            return (
              <MobileNavItem
                key={`m-${id}`}
                href={`#${id}`}
                $active={activeSection === id}
                onClick={(e) => handleNavClick(e, id)}
              >
                <span>{t(key)}</span>
                <span style={{ fontSize: '0.85rem', opacity: 0.6 }}>→</span>
              </MobileNavItem>
            );
          })}
        </MobileNavList>

        <MobileControlsRow>
          <LangPill>
            <LangButton type="button" $active={language === 'en'} onClick={() => setLanguage('en')}>
              EN
            </LangButton>
            <LangButton type="button" $active={language === 'de'} onClick={() => setLanguage('de')}>
              DE
            </LangButton>
          </LangPill>

          <ThemeNavBtn
            type="button"
            onClick={toggleTheme}
            aria-label={isDark ? t('toggleThemeLight') : t('toggleThemeDark')}
          >
            {isDark ? (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="5" />
                <line x1="12" y1="1" x2="12" y2="3" />
                <line x1="12" y1="21" x2="12" y2="23" />
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                <line x1="1" y1="12" x2="3" y2="12" />
                <line x1="21" y1="12" x2="23" y2="12" />
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
              </svg>
            ) : (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
              </svg>
            )}
          </ThemeNavBtn>
        </MobileControlsRow>
      </MobileDrawer>
    </>
  );
};

export default Navigation;
